import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { ArrowLeft, Plus, FileText, Copy, GraduationCap, Loader2, MoreHorizontal, Pencil, Trash2, Printer } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const StudentDetailPage = ({ 
  student, 
  onBack, 
  onCreateRubric, 
  onSelectRubric,
  onAddFromTemplate
}) => {
  const [rubrics, setRubrics] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchRubrics = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/students/${student.id}/rubrics`);
      setRubrics(response.data);
    } catch (err) {
      console.error('Error fetching student rubrics:', err);
      toast.error('Failed to load rubrics');
    } finally {
      setLoading(false);
    }
  }, [student.id]);

  useEffect(() => {
    fetchRubrics();
  }, [fetchRubrics]);

  const handleDeleteRubric = async (rubricId) => {
    if (!window.confirm('Delete this rubric?')) return;
    try {
      await axios.delete(`${API}/rubrics/${rubricId}`);
      setRubrics(prev => prev.filter(r => r.id !== rubricId));
      toast.success('Rubric deleted');
    } catch (err) {
      toast.error('Failed to delete rubric');
    }
  };

  const handleDuplicateRubric = async (rubricId) => {
    try {
      const response = await axios.post(`${API}/rubrics/${rubricId}/duplicate`, null, {
        params: { student_id: student.id }
      });
      setRubrics(prev => [...prev, response.data]);
      toast.success('Rubric duplicated');
    } catch (err) {
      toast.error('Failed to duplicate rubric');
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Back Button */}
      <Button
        variant="ghost"
        onClick={onBack}
        className="mb-4 -ml-2"
        data-testid="back-to-students-btn"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Students
      </Button>

      {/* Student Info Card */}
      <div className="bg-white rounded-2xl border border-border p-6 mb-6">
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
            <GraduationCap className="w-7 h-7 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl font-semibold">{student.display_name}</h1>
            <p className="text-muted-foreground">{student.grade}</p>
            {student.notes && (
              <p className="text-sm text-muted-foreground mt-2">{student.notes}</p>
            )}
          </div>
        </div>
      </div>

      {/* Rubrics Section */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold">Rubrics</h2>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={() => onAddFromTemplate(student)}
            className="rounded-xl"
            data-testid="add-from-template-btn"
          >
            <Copy className="w-4 h-4 mr-2" />
            Add from Template
          </Button>
          <Button 
            onClick={() => onCreateRubric(student)}
            className="rounded-xl"
            data-testid="create-rubric-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Rubric
          </Button>
        </div>
      </div>

      {/* Rubrics List */}
      {loading ? (
        <div className="flex items-center justify-center h-32">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : rubrics.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-2xl border border-border">
          <FileText className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" strokeWidth={1} />
          <h3 className="text-lg font-medium text-muted-foreground">No rubrics yet</h3>
          <p className="text-sm text-muted-foreground mt-1 mb-6">
            Create a new rubric or add one from your templates
          </p>
          <div className="flex gap-2 justify-center">
            <Button 
              variant="outline" 
              onClick={() => onAddFromTemplate(student)}
              className="rounded-xl"
            >
              <Copy className="w-4 h-4 mr-2" />
              From Template
            </Button>
            <Button 
              onClick={() => onCreateRubric(student)}
              className="rounded-xl"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create New
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {rubrics.map((rubric) => (
            <div
              key={rubric.id}
              className="bg-white rounded-xl border border-border p-4 hover:border-primary/30 transition-colors cursor-pointer group"
              onClick={() => onSelectRubric(rubric)}
              data-testid={`rubric-card-${rubric.id}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium">{rubric.title}</h3>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs px-2 py-0.5 bg-muted rounded-full">{rubric.category}</span>
                    <span className="text-xs text-muted-foreground">
                      {rubric.domains?.length || 0} domains • {rubric.number_of_levels} levels
                    </span>
                  </div>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                    <Button variant="ghost" size="sm" className="opacity-0 group-hover:opacity-100 h-8 w-8 p-0">
                      <MoreHorizontal className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onSelectRubric(rubric); }}>
                      <Pencil className="w-4 h-4 mr-2" />
                      Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleDuplicateRubric(rubric.id); }}>
                      <Copy className="w-4 h-4 mr-2" />
                      Duplicate
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={(e) => { e.stopPropagation(); handleDeleteRubric(rubric.id); }}
                      className="text-destructive"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
